/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
    createBrowserRouter,
    Link,
    Navigate,
    Outlet,
    redirect,
    RouteObject,
    useNavigate,
    useParams,
} from 'react-router';

import { AuthenticationGuard } from './components/auth/AuthenticationGuard';
import GeneralSettings from './components/organisation/GeneralSettings';
import Keys from './components/organisation/Keys';
import MembersSettings from './components/organisation/MembersSettings';
import NotificationSettings from './components/organisation/NotificationSettings';
import SubscriptionsSettings from './components/organisation/SubscriptionsSettings';
import { useUser } from './net/commons';
import Admin from './pages/Admin';
import CreateProject from './pages/CreateProject';
import Forbidden from './pages/Forbidden';
import Organisation from './pages/Organisation';
import OrgSetup from './pages/OrgSetup';
import Story from './pages/projects/admin/Story';
import StoryArchived from './pages/projects/admin/StoryArchived';
import CreateSuite from './pages/projects/CreateSuite';
import Executions from './pages/projects/Executions';
import { LiveAgentSession } from './pages/projects/LiveAgentSession';
import Projects from './pages/projects/Projects';
import ProjectSettings from './pages/projects/ProjectSetting';
import RedirectToRun from './pages/projects/RedirectToRun';
import RunViewer from './pages/projects/RunViewer';
import StoryPage from './pages/projects/Story';
import Suite from './pages/projects/Suite';
import Suites from './pages/projects/Suites';
import SuiteSettings from './pages/projects/SuiteSettings';
import Root from './pages/Root';
import Terms from './pages/Terms';

const projectRoutes: RouteObject[] = [
    {
        path: '/projects/',
        element: <Projects />,
    },
    {
        path: '/project/create',
        element: <CreateProject />,
    },
    {
        path: '/project/:projectSlug',
        element: <Suites />,
    },
    {
        path: '/project/:projectSlug/settings',
        element: <ProjectSettings />,
    },
];

const RedirectToExecutions = () => {
    const router = useNavigate();
    const { projectSlug, suiteSlug } = useParams();
    useEffect(() => {
        router(`/suite/${projectSlug}/${suiteSlug}/executions`);
    }, []);
    return <div />;
};

const suiteRoutes: RouteObject[] = [
    {
        path: '/suite/:projectSlug/create',
        element: <CreateSuite />,
    },
    {
        path: '/project/:projectSlug/suites',
        element: <Suites />,
    },
    {
        path: '/suite/:projectSlug/:suiteSlug',
        element: <RedirectToExecutions />,
    },
    {
        path: '/suite/:projectSlug/:suiteSlug/settings',
        element: <SuiteSettings />,
    },
];

const storyRoutes: RouteObject[] = [
    {
        path: '/story/:projectSlug/:suiteSlug/:storySlug',
        element: <StoryPage />,
    },
    {
        path: '/story/:projectSlug/:suiteSlug/:storySlug/:storyId',
        element: <StoryArchived />,
    },
];

const RoutesWithAdminPermissions = ({ children }: { children?: ReactNode }) => {
    const { isLoading, user } = useUser();
    if (isLoading) return null;
    const isAdmin = user?.isSuperUser;
    const isImpersonated = user?.impersonatedBy;
    const hasAdminPermissions = isAdmin || isImpersonated;
    if (hasAdminPermissions) {
        return children ? <>{children}</> : <Outlet />;
    }
    return <Navigate to="/" />;
};

const adminRoutes: RouteObject[] = [
    {
        path: '/admin',
        element: <RoutesWithAdminPermissions />,
        children: [
            {
                path: '/admin',
                element: <Admin />,
            },
            {
                path: '/admin/runs/:runId',
                element: <RedirectToRun />,
            },
            {
                path: '/admin/story/:projectSlug/:suiteSlug/create',
                element: <Story />,
            },
            {
                path: '/admin/story/:projectSlug/:suiteSlug/:storySlug',
                element: <Story isEditing />,
            },
        ],
    },
];

const orgRoutes: RouteObject[] = [
    {
        path: '/organisation',
        element: <Organisation />,
        children: [
            {
                index: true,
                loader() {
                    return redirect('general');
                },
            },
            {
                path: 'general',
                element: <GeneralSettings />,
            },
            {
                path: 'members',
                element: <MembersSettings />,
            },
            {
                path: 'notification',
                element: <NotificationSettings />,
            },
            {
                path: 'subscriptions',
                element: <SubscriptionsSettings />,
            },
            {
                path: 'keys',
                element: <Keys />,
            },
        ],
    },
];

export const GlobalError = ({ error }: { error?: Error }) => (
    <div>
        <h1>You might be lost</h1>
        <p>
            Do you want to{' '}
            <Link to="/" className="underline">
                go back home?
            </Link>
        </p>

        {error && (
            <p style={{ marginTop: '16px' }}>
                An error occurred:
                <pre style={{ padding: '0.5rem', backgroundColor: 'rgba(200, 200, 200, 0.5)' }}>{error.stack}</pre>
            </p>
        )}
    </div>
);

export const router = createBrowserRouter([
    {
        path: '/',
        element: <AuthenticationGuard component={Root} />,
        children: [
            {
                path: '/',
                element: <Projects />,
            },
            {
                path: '/create-project',
                element: <CreateProject />,
            },
            ...orgRoutes,
            ...projectRoutes,
            ...suiteRoutes,
            ...storyRoutes,
            ...adminRoutes,
            {
                path: '*',
                element: <GlobalError />,
            },
        ],
    },
    {
        path: '/',
        element: <AuthenticationGuard component={() => Root({ fullScreen: true })} />,
        children: [{ path: '/suite/:projectSlug/:suiteSlug/executions', element: <Executions /> }],
    },
    {
        path: '/',
        element: <AuthenticationGuard component={() => Root({ fullScreen: true })} />,
        children: [
            {
                path: '/suite/:projectSlug/:suiteSlug/stories',
                element: <Suite />,
            },
        ],
    },
    {
        path: '/',
        element: <AuthenticationGuard component={() => Root({ fullScreen: true })} />,
        children: [
            {
                path: '/suite/:projectSlug/:suiteSlug/blocks',
                element: <Suite block />,
            },
        ],
    },
    {
        path: '/',
        element: <AuthenticationGuard component={() => Root({ fullScreen: true })} />,
        children: [
            {
                path: '/story/:projectSlug/:suiteSlug/live/:storySlug?',
                element: (
                    <RoutesWithAdminPermissions>
                        <LiveAgentSession />
                    </RoutesWithAdminPermissions>
                ),
            },
        ],
    },
    {
        path: '/',
        element: <AuthenticationGuard component={() => Root({ fullScreen: true })} />,
        children: [
            {
                path: '/story/:projectSlug/:suiteSlug/:storySlug/edit-live',
                element: (
                    <RoutesWithAdminPermissions>
                        <LiveAgentSession isEditing />
                    </RoutesWithAdminPermissions>
                ),
            },
        ],
    },
    {
        path: '/',
        element: <AuthenticationGuard component={() => Root({ fullScreen: true })} />,
        children: [
            {
                path: '/run/:projectSlug/:suiteSlug/:executionId/:runId',
                element: <RunViewer />,
            },
        ],
    },
    {
        path: '/org-setup',
        element: <AuthenticationGuard component={OrgSetup} />,
    },
    {
        path: '/forbidden',
        element: <AuthenticationGuard component={Forbidden} />,
    },
    {
        path: '/terms',
        element: <Terms />,
    },
]);
